import "./Dashboard.css";
import React, { useEffect, useLayoutEffect, useRef } from "react";
import { NavLink, useLocation, matchPath } from "react-router-dom";
import { managementSections } from "./AdminSidebarData.js";
import { useAuth } from "../../authentication/AuthContext";

const UserBox = ({ username }) => (
  <div className="user-box text-center">
    <p className="cursor-pointer text-dark h5 mt-0 mb-1 d-block">{username}</p>
  </div>
);

const SIDEBAR_SCROLL_KEY = "adminSidebarScroll";

// sessionStorage throws outright in some privacy modes, so every access is
// guarded — a sidebar that cannot remember its position is still a working
// sidebar.
const readSavedScroll = () => {
  try {
    const raw = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch (e) {
    return 0;
  }
};

const writeSavedScroll = (value) => {
  try {
    sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(value));
  } catch (e) {
    /* not worth surfacing */
  }
};

const SideBarContent = ({ username }) => {
  const location = useLocation();
  const activeLinkRef = useRef(null);
  const scrollerRef = useRef(null);
  const restoredRef = useRef(false);
  // Both come from the server-verified AuthContext, never from localStorage.
  const { userType, can } = useAuth();

  // An item shows when the user's portal matches (if the item names one) and
  // they hold the permission that loads its page.
  const isVisible = (item) => {
    if (item.portals && !item.portals.includes(userType)) return false;
    return can(item.permission);
  };

  // Function to check if the current route matches (including subpaths)
  const isActivePath = (path) => matchPath({ path, end: false }, location.pathname);

  // Sections with nothing visible in them are dropped entirely, so a role with
  // no Marketing permissions doesn't get a bare "Marketing" heading.
  const visibleSections = managementSections
    .map(section => ({ ...section, items: section.items.filter(isVisible) }))
    .filter(section => section.items.length > 0);

  // The list is empty on the first render of a session: AuthContext resolves the
  // permission set asynchronously, and every item is permission-gated. Restoring
  // a scroll offset against a zero-height container would silently clamp to 0, so
  // the restore waits for there to be something to scroll.
  const itemCount = visibleSections.reduce((n, section) => n + section.items.length, 0);

  // Runs before paint, so the restored position is the first thing drawn rather
  // than a visible jump from the top.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || restoredRef.current || itemCount === 0) return;
    restoredRef.current = true;

    const saved = readSavedScroll();
    if (saved > 0) {
      // Put it back exactly where the user left it. The item they just clicked
      // is where they clicked it — nothing should move.
      scroller.scrollTop = saved;
      return;
    }

    // Nothing saved: a fresh tab, or a deep link straight into a page far down
    // the menu. Bring the active item into view — but only when it isn't already,
    // and instantly rather than animated.
    //
    // Measured with rects instead of offsetTop: the link's offsetParent is the
    // position:fixed .left-side-menu, not this scroll container, so offsetTop
    // would be off by the container's own offset and padding.
    const link = activeLinkRef.current;
    if (!link) return;

    const linkRect = link.getBoundingClientRect();
    const boxRect = scroller.getBoundingClientRect();
    const hidden = linkRect.top < boxRect.top || linkRect.bottom > boxRect.bottom;
    if (hidden) {
      scroller.scrollTop +=
        linkRect.top - boxRect.top - (scroller.clientHeight - linkRect.height) / 2;
    }
  }, [itemCount]);

  // Park the offset on every scroll so the next mount has something to restore.
  // Coalesced into one write per frame — scroll fires far faster than useful.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    let frame = null;
    const onScroll = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        writeSavedScroll(scroller.scrollTop);
      });
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <>
      {/* <UserBox username={username} /> */}

      <div id="sidebar-menu" className="ScrollY" ref={scrollerRef}>
        <ul className="side-menu" id="side-menu">
          {visibleSections.map((section, index) => (
            <React.Fragment key={index}>
              <li className="menu-title mt-0" style={{ fontWeight: "bold" }}>
                {section.title}
              </li>
              {section.items.map((item, itemIndex) => (
                <li key={itemIndex} className="side-nav-item icon-clr">
                  <NavLink
                    to={item.path}
                    className="side-nav-link-ref icon-clr"
                    style={{ textDecoration: "none" }}
                    activeclassname="active"
                    ref={isActivePath(item.path) ? activeLinkRef : null}
                  >
                    <i className="feather feather-calendar">{item.icon}</i>
                    <span> {item.title} </span>
                  </NavLink>
                </li>
              ))}
            </React.Fragment>
          ))}
        </ul>
      </div>
    </>
  );
};

export default SideBarContent;
